import TrelloModel, {
  TrelloModelAttributes,
} from 'app/scripts/models/internal/trello-model';
import { featureFlagClient } from '@trello/feature-flag-client';

const syncQueue = new Map();

interface PluginDataAttributes extends TrelloModelAttributes {
  idPlugin: string;
  scope: string;
  idModel: string;
}

class PluginData extends TrelloModel<PluginDataAttributes> {
  static initClass() {
    this.prototype.typeName = 'PluginData';
  }

  urlRoot() {
    return `/1/${this.get('scope')}/${this.get('idModel')}/pluginData`;
  }

  /**
   * Override the update method for this model, and ensure we always wait for the
   * in-flight persist to complete, before starting another. The reason for this
   * is due to the pluginData design, and the API used by power-ups to interact
   * with it, clobbering data is possible. To prevent this, we use a
   * `If-Unmodified-Since` header, to prevent updates using a stale data as a
   * basis.
   * This header relies on the `model.dateLastUpdated` value matching what the
   * server knows. If we don't wait for the in-flight set to complete, our
   * `dateLastUpdated` will not match the soon-to-be-updated value, and thus,
   * server will reject our request.
   */
  // @ts-ignore
  update(key, value, opts, next) {
    const id = this.get('id');
    if (syncQueue.has(id)) {
      // Queue the most current sync to happen once the in-progress one is
      // finished
      let newNext = next;
      const previouslyQueuedUpdate = syncQueue.get(id);
      if (previouslyQueuedUpdate) {
        newNext = function () {
          previouslyQueuedUpdate.next?.(...arguments);
          next?.(...arguments);
        };
      }
      syncQueue.set(id, {
        fx: () => {
          this.update(key, value, opts, newNext);
        },
        next,
      });
      // Immediately update the model, to avoid losing any information if we
      // drop PUT requests, due to the sync queue
      super.update(key, value, {
        skipPersist: true,
      });
      return;
    }
    // We have an in-flight sync, but no sync queued up
    syncQueue.set(id, null);
    const runNextSync = () => {
      const queuedSync = syncQueue.get(id);
      syncQueue.delete(id);
      queuedSync?.fx();
    };

    return super.update(key, value, opts, () => {
      runNextSync();
      next?.(...arguments);
    });
  }

  shouldSendIfUnmodified() {
    const idPlugin = this.get('idPlugin');
    const allowedPlugins: string[] = featureFlagClient.get(
      'workflowers.prevent-stale-plugindata-writes',
      [],
    );
    return allowedPlugins.includes(idPlugin);
  }
}
PluginData.initClass();

export { PluginData };
